import { useSuperMutation } from '@vadiun/react-hooks-legacy';
import { useViajesTraslado } from 'app/viaje/services';
import { useHistory } from 'react-router-dom';

import { 
  useDistanceAndPriceTransferReservationClientBusinessCalculator,
  TravelEstimationResult
} from 'app/viaje/services/distanceAndPriceViajeTrasladoCalculator';
import { ReservaTrasladoFormWrapper } from 'app/viaje/viajeTraslado/viajeTraslado/components/ViajeTrasladoFormWrapper';
import PageContainer from 'layout/components/PageContainer';
import { useCallback, useEffect, useState } from 'react';
import { Card } from 'shared/components';
import { CreatedReservationsModal } from '../components/CreatedReservationsModal';
import TransferReservationBatchForm from '../forms/batch/TransferReservationBatchForm';
import {
  TransferReservationBatchFormType,
  transferReservationBatchFormBuilder
} from '../forms/batch/TransferReservationBatchFormType';
import { useTransferReservationStore } from '../services/TransferReservationSingleStore';
import { BusType, transferReservationBuilder } from '../models/TransferReservation';
import { ViajeTrasladoTipoServicio } from '../models';

export const TransferReservationClientBusinessCopyPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [createdReservationsData, setCreatedReservationsData] = useState<any[] | null>(null);
  const [optionsBuses, setOptionsBuses] = useState<BusType[]>([]);
  const [distanceAndCost, setDistanceAndCost] = useState<TravelEstimationResult[]>([]);
  const history = useHistory();
  const ViajeTrasladoService = useViajesTraslado();
  const { calculateDistanceAndCost } =
    useDistanceAndPriceTransferReservationClientBusinessCalculator();

  const addTrasladoMutation = useSuperMutation(
    ViajeTrasladoService.addViajesTraslado,
    { 
      showSpinner: true,
      onMutationFinish: (response) => {
        setCreatedReservationsData(response.data.map(r => transferReservationBuilder.fromBackEnd(r)));
        setModalOpen(true);
      }
    }
  );

  const { reservation: initialValues } = useTransferReservationStore();

  const parsedInitialValues = {
    ...initialValues!,
    bus_vehicle_id: undefined,
    reason_reserve_past: initialValues?.reason_reserve_past ?? undefined,
    solicitante: {
      phone: initialValues?.solicitante.phone ?? '',
      name: initialValues?.solicitante.name ?? '',
      email: initialValues?.solicitante.email ?? '',
      id: initialValues?.solicitante.id
    },
    viajes: [
      {
        fechaYHoraDeLlegada: initialValues?.fechaYHoraDeLlegada!,
        minutosPreviosASalida: initialValues?.minutosPreviosASalida!,
        puntos: initialValues?.puntos!,
        prioridad: initialValues?.prioridad ?? false,
        hasOutSite: initialValues?.hasOutSite ?? false,
        tipoServicio: initialValues?.tipoServicio ?? ViajeTrasladoTipoServicio.EJECUTIVO,
        fixedRate: initialValues?.fixedRate ?? null,
        bus_provider_price: initialValues?.bus_provider_price ?? 0,
        bus_customer_price: initialValues?.bus_customer_price ?? 0,
        bus_is_outsourced: initialValues?.bus_is_outsourced ?? false,
        bus_observation: initialValues?.bus_observation ?? '',
        bus_type_id: initialValues?.bus_type_id ?? 0,
        bus_vehicle_id: initialValues?.bus_vehicle_id ?? 0
      }
    ]
  };

  const createViajeTraslado = useCallback(
    async (
      trasladoFormValues: TransferReservationBatchFormType,
      clientBusinessId: number
    ) => {
      const ViajeTraslado =
        transferReservationBatchFormBuilder.toModel(trasladoFormValues);
      await addTrasladoMutation.mutate(ViajeTraslado, clientBusinessId);
    },
    [addTrasladoMutation]
  );

  function estimatePrice(
    trasladoFormValues: TransferReservationBatchFormType,
    clientBusinessId: number
  ): Promise<TravelEstimationResult[]> {
    const estimationsPromise = trasladoFormValues.viajes.map((viaje, index) => {
      if (viaje.tipoServicio === ViajeTrasladoTipoServicio.SERVICIOS_GRUPALES) {
        return {
          isServiceGrupal: true,
          viajeIndex: index + 1,
          message: `Es Servicio Grupal*`
        };
      }
      return calculateDistanceAndCost(
        {
          arrivalDate: viaje.fechaYHoraDeLlegada,
          hasOutSite: viaje.hasOutSite,
          points: viaje.puntos.map((p) => ({
            lat: p.domicilio.latitud,
            lng: p.domicilio.longitud,
            minutesWaiting: Number(p.espera)
          })),
          serviceType: viaje.tipoServicio,
          rateName: viaje.fixedRate?.name,
          payWithCash: trasladoFormValues.pay_with_cash,
          entryChannel: trasladoFormValues.canalIngreso
        },
        clientBusinessId
      );
    });
    return Promise.all(estimationsPromise);
  }

  const getOptionsBuses = useCallback(async () => {
    const buses = await ViajeTrasladoService.getOptionsBuses();
    setOptionsBuses(buses);
  }, [ViajeTrasladoService]);

  useEffect(() => {
    getOptionsBuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageContainer>
      <Card className="w-full" title="Nuevo traslado">
        <div className="p-8">
          <ReservaTrasladoFormWrapper<TransferReservationBatchFormType>
            initialValues={parsedInitialValues!}
            onSubmit={createViajeTraslado}
            calculateDistanceAndCost={estimatePrice}
          >
            {(params) => (
              <TransferReservationBatchForm
                optionsBuses={optionsBuses}
                {...params}
              />
            )}
          </ReservaTrasladoFormWrapper>
        </div>
      </Card>
      <CreatedReservationsModal
        open={modalOpen}
        reservationsData={createdReservationsData}
        onFinish={() => {
          setModalOpen(false);
          setTimeout(() => {
            history.push('/main/operations/coordination/pending-trips');
          }, 300);
        }}
      />
    </PageContainer>
  );
};
